import React, { useCallback, useState } from 'react';
import { useAppDispatch } from 'app/helpers/hooks';
import { Link } from 'react-router-dom';
import * as Routes from 'app/constants/Routes';
import { Formik, Form } from 'formik';
import cx from 'classnames';
import Input from 'app/components/common/formik/Input';
import { RegistrationSchemaStep4 } from 'app/helpers/validators';
import { useSegmentTracking } from 'app/utils/useSegmentTracking';
import { SEGMENT_KING_EVENTS } from 'app/constants/SegmentEvents';
import { Button } from 'mui';
import { push } from 'connected-react-router/immutable';
import { hotjarIdentity, segmentAlias } from 'app/utils';
import { guestRegistrationTermsSubmitted, NOOP } from 'app/actions/customer';
import Cookies from 'js-cookie';

const AccountRegistrationStep = ({ allValues, trackSegmentEvent = false }) => {
  useSegmentTracking(trackSegmentEvent ? SEGMENT_KING_EVENTS.VIEWED_ACCOUNT_CREATION : undefined);
  const [emailExist, setEmailExist] = useState(false);

  const dispatch = useAppDispatch();

  const onSubmit = useCallback(
    async (values, form) => {
      const email = values.email.trim();
      const action = {
        params: {
          ...Object.values(allValues as Record<string, Record<string, any>>).reduce(
            (reduced, val) => ({ ...reduced, ...val }),
            {},
          ),
          ...values,
          email,
          href: window.location.href,
          referrer: window.referrer,
        },
        context: {
          onSuccessActionCreator: ({ json: { registration_feedback, product, user_id } }) => {
            if (registration_feedback === 'need_sign_in') {
              form.setFieldValue('password', '');
              form.setFieldTouched('password', false);
              form.setFieldValue('password_confirmation', '');
              form.setFieldTouched('password_confirmation', false);
              setEmailExist(true);
            } else {
              if (user_id) {
                hotjarIdentity(user_id);
                segmentAlias(user_id);
              }

              if (registration_feedback) {
                return push(`${Routes.Registration}?registration_feedback=${registration_feedback}&product=${product}`);
              }

              window.location.href = Routes.ContinueOnboarding;
            }

            return { type: NOOP };
          },
          onFailureActionCreator: ({ json: { errors } }) => {
            form.setErrors(errors);

            return { type: NOOP };
          },
        },
      };

      Cookies.set('email', email);
      await dispatch(guestRegistrationTermsSubmitted(action));
    },
    [dispatch, allValues],
  );

  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
        password_confirmation: '',
      }}
      onSubmit={onSubmit}
      validationSchema={RegistrationSchemaStep4}
    >
      {({
        isSubmitting,
        errors,
        setFieldValue,
      }: {
        isSubmitting: boolean;
        errors: {
          api?: string;
        };
        setFieldValue: (field: string, value: any) => void;
      }) => (
        <Form>
          <div className="flex flex-col gap-y-2">
            <Input
              onChange={(e) => setFieldValue('email', e.target.value.trim())}
              id="email"
              label="Email Address"
              name="email"
              placeholder="Email (used as username)"
              className="text-field"
              disabled={emailExist}
              errorText={
                emailExist && (
                  <>
                    This email already exists. Would you like to <Link to={Routes.Login}>Log in</Link>?
                  </>
                )
              }
            />
          </div>
          <hr className="mt16 mb16" />
          <div
            className={cx('flex flex-col gap-y-2', {
              hidden: emailExist,
            })}
          >
            <Input
              id="password"
              label="Password"
              name="password"
              placeholder="Enter a Password"
              type="password"
              className="text-field"
              disabled={emailExist}
            />
            <Input
              id="password_confirmation"
              label="Confirm Password"
              name="password_confirmation"
              type="password"
              placeholder="Re-enter Password"
              className="text-field"
              disabled={emailExist}
            />
          </div>

          {errors && errors.api && (
            <div className="border border-main-danger text-main-danger px-4 py-2 rounded-xl">{errors.api}</div>
          )}

          {emailExist ? (
            <Button onClick={() => dispatch(push(Routes.Login))}>Log In</Button>
          ) : (
            <>
              <Button
                disabled={isSubmitting}
                className="mt24"
                type="submit"
                data-testid="registration-step-4-submit"
                text="Next"
              >
                Next
              </Button>

              <div className="links mt12">
                <div className="links__item">
                  Already have an account? <Link to={Routes.Login}>Log In</Link>
                </div>
              </div>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default AccountRegistrationStep;
